import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Gift, Copy, CheckCircle, ShareNetwork, Sparkle, Clock, ArrowLeft,
  Envelope, ChatCircleText, FacebookLogo, XLogo, WhatsappLogo, CurrencyDollar,
  ArrowsClockwise,
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReferralsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/login'); return; }
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/referrals/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Could not load referrals', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  useEffect(() => { fetchData(); }, []);

  const referralUrl = data?.referral_code
    ? `${window.location.origin}/?ref=${data.referral_code}`
    : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      toast({ title: 'Link copied!', description: 'Share it anywhere to earn credits.' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Copy failed', variant: 'destructive' });
    }
  };

  const shareMsg = data
    ? `I'm using ConstructHelp to ${data.referred_by ? 'manage projects' : 'find pros and post jobs'} — sign up with my link and we both get $${data.signup_reward} in credits: ${referralUrl}`
    : '';

  const openShare = (platform) => {
    if (!data) return;
    const encoded = encodeURIComponent(shareMsg);
    const urlEnc = encodeURIComponent(referralUrl);
    let url = '';
    if (platform === 'whatsapp') url = `https://wa.me/?text=${encoded}`;
    else if (platform === 'twitter') url = `https://twitter.com/intent/tweet?text=${encoded}`;
    else if (platform === 'facebook') url = `https://www.facebook.com/sharer/sharer.php?u=${urlEnc}`;
    else if (platform === 'sms') url = `sms:?&body=${encoded}`;
    else if (platform === 'email') url = `mailto:?subject=${encodeURIComponent('Join me on ConstructHelp')}&body=${encoded}`;
    if (url) window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <Clock size={32} weight="bold" className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 pb-16" data-testid="referrals-page">
      {/* Header */}
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-white/70 hover:text-white font-bold"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} weight="bold" /> Back
          </button>
          <h1 className="text-lg sm:text-2xl font-black uppercase text-white flex items-center gap-2">
            <Gift size={22} weight="fill" className="text-[#D95800]" />
            Refer &amp; Earn
          </h1>
          <button
            onClick={fetchData}
            className="text-white/70 hover:text-white p-2"
            data-testid="refresh-btn"
            title="Refresh"
          >
            <ArrowsClockwise size={18} weight="bold" />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">

        {/* Hero / Credits Card */}
        <div className="bg-gradient-to-br from-[#D95800] to-[#B84A00] border-4 border-black rounded-xl p-6 sm:p-8 shadow-[6px_6px_0px_#000] text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm flex-shrink-0">
              <Sparkle size={28} weight="fill" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl sm:text-3xl font-black uppercase mb-1">Earn ${data?.signup_reward || 25} per friend</h2>
              <p className="text-white/90 text-sm sm:text-base">
                Both you and your friend get <strong>${data?.signup_reward || 25} in credits</strong> when they sign up,
                plus an extra <strong>${data?.action_reward || 25}</strong> when they post their first job or accept a lead.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-3 gap-3">
            <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 text-center">
              <p className="text-2xl sm:text-3xl font-black" data-testid="credits-balance">${data?.referral_credits || 0}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-white/80 mt-1">Your Balance</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 text-center">
              <p className="text-2xl sm:text-3xl font-black">{data?.stats?.total_signed_up || 0}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-white/80 mt-1">Signups</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-lg p-3 text-center">
              <p className="text-2xl sm:text-3xl font-black">{data?.stats?.total_completed || 0}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-white/80 mt-1">Active</p>
            </div>
          </div>
        </div>

        {/* Referral Link */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
          <h3 className="text-xs font-black uppercase text-neutral-500 mb-3 flex items-center gap-2">
            <ShareNetwork size={14} weight="bold" /> Your Personal Link
          </h3>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={referralUrl}
              className="flex-1 h-12 px-4 text-sm font-mono bg-neutral-50 dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white truncate"
              data-testid="referral-link-input"
              onClick={(e) => e.target.select()}
            />
            <button
              onClick={handleCopy}
              className={`h-12 px-5 font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all flex items-center gap-2 ${
                copied ? 'bg-emerald-500 text-white' : 'bg-[#D95800] hover:bg-[#B84A00] text-white'
              }`}
              data-testid="copy-link-btn"
            >
              {copied ? <CheckCircle size={18} weight="bold" /> : <Copy size={18} weight="bold" />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-neutral-500 mt-2">
            Code: <span className="font-mono font-bold text-neutral-900 dark:text-white">{data?.referral_code}</span>
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-5">
            {[
              { id: 'whatsapp', icon: WhatsappLogo, label: 'WhatsApp', color: 'bg-emerald-500 hover:bg-emerald-600' },
              { id: 'sms', icon: ChatCircleText, label: 'Text', color: 'bg-blue-500 hover:bg-blue-600' },
              { id: 'email', icon: Envelope, label: 'Email', color: 'bg-neutral-700 hover:bg-neutral-800' },
              { id: 'twitter', icon: XLogo, label: 'X', color: 'bg-black hover:bg-neutral-800' },
              { id: 'facebook', icon: FacebookLogo, label: 'Facebook', color: 'bg-[#1877F2] hover:bg-[#1465D0]' },
            ].map(s => (
              <button
                key={s.id}
                onClick={() => openShare(s.id)}
                className={`flex items-center justify-center gap-1.5 h-10 px-3 ${s.color} text-white text-xs font-bold rounded-lg transition-all`}
                data-testid={`share-${s.id}-btn`}
              >
                <s.icon size={16} weight="bold" /> {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-6">
          <h3 className="font-black text-lg text-neutral-900 dark:text-white mb-4">How It Works</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { n: 1, title: 'Share your link', desc: 'Send your personal link to friends, customers, or fellow pros.' },
              { n: 2, title: 'They sign up', desc: `You both instantly get $${data?.signup_reward || 25} in lead/job credits.` },
              { n: 3, title: 'They go active', desc: `When they post their first job or accept a lead, both sides earn $${data?.action_reward || 25} more.` },
            ].map(s => (
              <div key={s.n} className="border-l-4 border-[#D95800] pl-4">
                <div className="text-3xl font-black text-[#D95800]">{s.n}</div>
                <p className="font-bold text-sm text-neutral-900 dark:text-white">{s.title}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Referral List */}
        <div className="bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-6">
          <h3 className="font-black text-lg text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
            Your Referrals
            <span className="text-xs font-normal text-neutral-500">({data?.referrals?.length || 0})</span>
          </h3>
          {(!data?.referrals || data.referrals.length === 0) ? (
            <div className="py-10 text-center">
              <Gift size={48} weight="bold" className="text-neutral-300 mx-auto mb-3" />
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                No referrals yet. Share your link to start earning!
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.referrals.map(r => (
                <div
                  key={r._id}
                  className="flex items-center justify-between gap-3 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg p-3"
                  data-testid={`referral-row-${r._id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                      r.status === 'first_action_completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-200 text-neutral-700'
                    }`}>
                      {r.referee_name ? r.referee_name.charAt(0).toUpperCase() : '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-neutral-900 dark:text-white truncate">{r.referee_name || 'New User'}</p>
                      <p className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate capitalize">
                        {r.referee_role || 'user'} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                      r.status === 'first_action_completed'
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                        : 'bg-amber-100 text-amber-800 border border-amber-300'
                    }`}>
                      {r.status === 'first_action_completed' ? '✓ Active' : 'Signed Up'}
                    </span>
                    <p className="text-xs font-black text-emerald-600 mt-1 flex items-center justify-end gap-1">
                      <CurrencyDollar size={10} weight="bold" /> {r.total_credits || 0}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
